import { motion } from "framer-motion";
import { Icon } from "./Icon";

interface NarrativeNotFoundProps {
  requestedReportId: string | null;
}

/**
 * Elegant "narrative not found" screen displayed when a requested report ID
 * doesn't match any available narratives or no report ID is provided.
 * Does not expose available report IDs for privacy.
 */
export function NarrativeNotFound({
  requestedReportId,
}: NarrativeNotFoundProps) {
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 text-white">
      {/* Subtle animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        className="relative z-10 flex max-w-md flex-col items-center text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Icon */}
        <motion.div
          className="mb-6 text-3xl flex h-20 w-20 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
        >
          <Icon icon="fass map" className="h-9 w-9 text-white/70" />
        </motion.div>

        {/* Headline */}
        <motion.h1
          className="mb-3 font-serif text-3xl font-semibold tracking-tight"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {requestedReportId ? "Report Not Found" : "Welcome"}
        </motion.h1>

        {/* Subtext */}
        <motion.p
          className="mb-6 text-lg font-light leading-relaxed text-white/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          {requestedReportId
            ? "The report you're looking for doesn't exist or may have expired."
            : "You'll need a valid report link to view your insights."}
        </motion.p>

        {/* Divider */}
        <motion.div
          className="mb-6 h-px w-24 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        />

        {/* Help text */}
        <motion.p
          className="text-sm text-white/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          {requestedReportId
            ? "Please check your link or contact your account manager."
            : "Contact your account manager for your personalized report link."}
        </motion.p>
      </motion.div>

      {/* Decorative gradient orbs */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-64 w-64 rounded-full bg-purple-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
    </div>
  );
}
